import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { IconLibrary } from '../display/IconLibrary/IconLibrary';
import { IconName } from '../display/IconLibrary/IconLibraryTypes';
import { useDisplayModeContext } from '../theme/display';
import { FlexRowListItem } from '../containers/ContentContainers/FlexRowListItem';
import { motion } from 'framer-motion';
import { useMobile } from '@act/utilities/mobile-context';
import { useSideMenuDrawer } from '@act/utilities/SideMenuDrawerContext';

interface NavigationListItemProps {
  text: string;
  icon?: IconName;
  postIcon?: IconName;
  isSelected?: boolean;
  onClick?: () => void;
}

export const NavigationListItem = ({
  text,
  icon,
  postIcon,
  isSelected,
  onClick,
}: NavigationListItemProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const { displayMode, isAnimating } = useDisplayModeContext();
  const { isMobile } = useMobile();
  const { closeDrawer } = useSideMenuDrawer();
  const isCompact = displayMode === 'compact';

  const labelVariants = {
    hidden: { opacity: 0, transition: { duration: 0.2 } },
    visible: { opacity: 1, transition: { duration: 0.2, delay: 0.5 } },
  };

  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <FlexRowListItem
        isSelected={isSelected}
        onClick={() => {
          onClick?.();
          if (isMobile) {
            closeDrawer();
          }
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: isCompact ? 0 : 2 }}
        >
          {icon && (
            <IconLibrary
              iconName={icon}
              size={32}
              useContrastColor={isHovered}
            />
          )}

          <Box
            sx={{
              minWidth: isCompact ? 0 : 150,
              maxWidth: isCompact ? 0 : 200,
              flex: isCompact ? 0 : 1,
            }}
          >
            <motion.div
              variants={labelVariants}
              animate={isCompact && !isAnimating ? 'hidden' : 'visible'}
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {!isCompact && (
                <Typography
                  component="span"
                  variant="bodyMedium"
                  sx={{
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'normal',
                  }}
                >
                  {text}
                </Typography>
              )}
              {postIcon && (
                <IconLibrary
                  iconName={postIcon}
                  weight="bold"
                  color="primary"
                  useContrastColor={isHovered}
                  size={16}
                />
              )}
            </motion.div>
          </Box>
        </Box>
      </FlexRowListItem>
    </Box>
  );
};
